import {
  type Update,
  type AbstractBatchedUpdates,
  type EmptyMetadata,
} from './abstract-batched-updates';

export class BatchedUpdates<T, Metadata = EmptyMetadata>
  implements AbstractBatchedUpdates<T, Metadata>
{
  private updates: Update<T, Metadata>[] = [];
  private handle: ReturnType<typeof setInterval> | undefined;
  private observer?: (data: Update<T, Metadata>) => void;

  constructor(
    private timeout: number,
    private merge: (input: Update<T, Metadata>[]) => Update<T, Metadata>
  ) {}

  addUpdate(update: Update<T, Metadata>) {
    this.updates.push(update);

    this.scheduleUpdate();
  }

  flush() {
    if (this.updates.length > 0) {
      const mergedUpdate = this.merge(this.updates);

      this.observer?.(mergedUpdate);

      this.updates = [];

      if (this.handle) {
        clearInterval(this.handle);
        this.handle = undefined;
      }
    }
  }

  // no-op, only exists for backwards compatibility
  onFlush(_flushObserver: () => void): this {
    return this;
  }

  private scheduleUpdate() {
    if (!this.handle) {
      this.handle = setInterval(() => {
        if (this.updates.length > 0) {
          const mergedUpdate = this.merge(this.updates);

          this.observer?.(mergedUpdate);

          this.updates = [];
        } else if (this.handle) {
          clearInterval(this.handle);
          this.handle = undefined;
        }
      }, this.timeout);
    }
  }

  onUpdate(observer: (data: Update<T, Metadata>) => void): this {
    this.observer = observer;
    return this;
  }

  offUpdate(): this {
    this.observer = undefined;
    return this;
  }

  static takeLast<T, Metadata>(timeout: number): BatchedUpdates<T, Metadata> {
    return new BatchedUpdates<T, Metadata>(
      timeout,
      (values: Update<T, Metadata>[]) => values[values.length - 1]
    );
  }

  destroy() {
    if (this.handle) {
      clearInterval(this.handle);
      this.handle = undefined;
    }
  }
}
